import { Injectable } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { MultiCartService, StateWithMultiCart, CartActions } from '@spartacus/cart/base/core'
import {
  ConfigurationService,
  GlobalMessageService,
  GlobalMessageType,
  UserIdService,
  WindowRef
} from '@spartacus/core'
import { map, skip, take } from 'rxjs/operators'
import { EbRequestedShippingDateAdapter } from '../../checkout/services/connectors/eb-requested-shipping-date.adapter'
import { EbAvailabilityAdapter } from '../../product/services/connectors/eb-availability.adapter'
import { EbCartModification } from '../interface/eb-cart.interface'
import { EbOrderEntry, EbQuickOrderEntry } from '../interface/eb-order-entry.interface'
import * as EbCartEntryAction from '../store/actions/eb-cart-entry.action'
import { getCartModifications } from '../store/selectors/eb-multi-cart.selector'

@Injectable({ providedIn: 'root' })
export class EbMultiCartService extends MultiCartService {
  constructor(
    protected globalMessageService: GlobalMessageService,
    protected store: Store<StateWithMultiCart>,
    protected userIdService: UserIdService,
    protected requestedShippingDateAdapter: EbRequestedShippingDateAdapter,
    protected ebAvailabilityAdapter: EbAvailabilityAdapter
  ) {
    super(store, userIdService)
  }

  static keepActiveCartIdInSync(
    store: Store<StateWithMultiCart>,
    configurationService: ConfigurationService,
    winRef: WindowRef
  ) {
    return () => {
      winRef.nativeWindow.addEventListener('storage', evt => {
        configurationService.unifiedConfig$.pipe(take(1)).subscribe(config => {
          const { baseSite } = config.context
          baseSite.forEach(site => {
            if (evt.key === `spartacus⚿${site}⚿cart`) {
              const newValue = JSON.parse(evt.newValue)
              const oldValue = JSON.parse(evt.oldValue)

              if (newValue?.active && newValue?.active !== oldValue?.active) {
                store.dispatch(new CartActions.SetActiveCartId(newValue?.active))
              }
            }
          })
        })
      })
    }
  }

  addEntry(userId: string, cartId: string, productCode: string, quantity: number): void {
    this.store.dispatch(
      new EbCartEntryAction.EbCartAddEntry({
        userId,
        cartId,
        productCode,
        quantity
      })
    )
    this.requestedShippingDateAdapter
      .setRequestedShippingDate(
        cartId,
        userId,
        null,
        false,
        false,
        false
      ).subscribe()

    this.ebAvailabilityAdapter.setEarliestShippingDate([]).subscribe()
  }

  addEntries(
    userId: string,
    cartId: string,
    entries: EbOrderEntry[] | EbQuickOrderEntry[]
  ): void {
    this.store.dispatch(
      new EbCartEntryAction.EbCartAddEntries({
        userId,
        cartId,
        entries
      })
    )
    this.requestedShippingDateAdapter
      .setRequestedShippingDate(
        cartId,
        userId,
        null,
        false,
        false,
        false
      ).subscribe()
    this.ebAvailabilityAdapter.setEarliestShippingDate([]).subscribe()
  }

  removeEntry(userId: string, cartId: string, entryNumber: number): void {
    this.store.dispatch(
      new EbCartEntryAction.EbCartRemoveEntry({
        userId,
        cartId,
        entryNumber: `${entryNumber}`
      })
    )
    this.requestedShippingDateAdapter
      .setRequestedShippingDate(
        cartId,
        userId,
        null,
        false,
        false,
        false
      ).subscribe()
    this.ebAvailabilityAdapter.setEarliestShippingDate([]).subscribe()
  }

  updateEntry(userId: string, cartId: string, entryNumber: number, quantity: number): void {
    if (quantity > 0) {
      this.store.dispatch(
        new EbCartEntryAction.EbCartUpdateEntry({
          userId,
          cartId,
          entryNumber: `${entryNumber}`,
          quantity
        })
      )
    } else {
      this.removeEntry(userId, cartId, entryNumber)
    }
  }

  filterByAddedEntriesError() {
    return this.store.pipe(
      select(getCartModifications),
      skip(1),
      map((modifications: EbCartModification[]) => {
        const erroneousCartModifications = modifications.filter(
          modification => modification.statusCode !== 'success'
        )
        if (erroneousCartModifications.length === 0) {
          this.globalMessageService.add(
            { key: 'quickOrder.message.success' },
            GlobalMessageType.MSG_TYPE_CONFIRMATION
          )
        } else if (erroneousCartModifications.length === modifications.length) {
          this.globalMessageService.add(
            { key: 'quickOrder.message.error' },
            GlobalMessageType.MSG_TYPE_ERROR
          )
        } else {
          this.globalMessageService.add(
            { key: 'quickOrder.message.warn' },
            GlobalMessageType.MSG_TYPE_WARNING
          )
        }
        return erroneousCartModifications
      })
    )
  }
}
